import { h, render, Component } from 'preact';
import util from 'preact-util';
import { observer } from 'mobx-preact';
import { toJS } from 'mobx';
import { IntlProvider, Text, Localizer, withText } from 'preact-i18n';
import Markdown from 'preact-markdown';
import { route } from 'preact-router';

import FileList from '../../components/fileViewer/list';
// import DyrejournalViewForm from '../../components/dyrejournal/viewForm';
import DyrejournalPrintForm from '../../components/dyrejournal/printForm';

import localUtil from '../../lib/util';
import { printViaIframe } from '../../lib/printHelper';

const MARKDOWN_OPTIONS = {
    pedantic: false,
    gfm: true,
    breaks: true,
    sanitize: false,
    smartLists: true,
    smartypants: true,
    xhtml: true,
    highlight: function(code, lang) {
        const hljs = require('highlight.js');
        const language = hljs.getLanguage(lang) ? lang : 'plaintext';
        return hljs.highlight(code, { language }).value;
    },
    langPrefix: 'hljs language-', // highlight.js css expects a top-level 'hljs' class.
};

import definitionNo from '../../languages/no.json';
import definitionEn from '../../languages/en.json';
import { appConfirm } from '../../lib/dialogs';
import { beginDialogAction } from '../../lib/dialogAction';

const countryMap = {
    no: definitionNo,
    en: definitionEn,
    default: definitionEn,
};

// Print CSS for the clinic forms (Innleggelse, Urinprøve, cremation, attests).
// These forms are rendered inside `.viewForm`. The app's own `.viewForm` print
// sizing lives in custom.css inside `@media print` blocks, but the iframe print
// path strips all `@media print` rules (see printHelper.getDocumentStyles) and its
// clean replacement CSS only styles `.receipt`/`.printPrescription`/`.print-section`
// — never `.viewForm`. Without this, the form prints at the 16px Bootstrap default
// and spills onto a 2nd page (especially with a clinic logo on top).
//
// Everything in the form is rem/em based, so reducing the root font-size uniformly
// scales the whole form down while preserving its layout and label positioning,
// keeping it on a single A4 page. The logo is capped so the header stays compact.
const FORM_PRINT_CSS = `
@media print {
    html {
        font-size: 11px !important;
    }
    .viewForm {
        color: black !important;
        background: #fff !important;
    }
    .viewForm img {
        max-height: 14mm !important;
        width: auto !important;
    }
}
`;



@observer
class FileListTable extends Component {
  	constructor(props) {
        super(props);
        this.state = {
        };
        this.mainContainer = null;
        this.calendar = null;
    }

    loadAll = async () => {
        const { fileStore } = this.props.stores;
        fileStore.load({
            query: {},
            skipUpdate: false,
        });
    }

    // Load the journal(s) for the animal this drawer was opened for, so an attest
    // (dødsattest etc.) can auto-fill its journal fields even when printed outside the
    // original visit. Kept in saleStore.attestJournals so it never clobbers the sale
    // page's own journal state.
    loadAttestJournals = async () => {
        const { animalId } = this.props;
        const { journalStore, saleStore } = this.props.stores;
        const { newSale = {} } = saleStore;

        // Clear first so a previously opened attest never flashes the wrong journal.
        saleStore.updateKeyValue('attestJournals', []);

        let animalIds = [];
        if (animalId) {
            animalIds = [parseInt(animalId, 10)];
        } else if (Array.isArray(newSale.animals)) {
            animalIds = newSale.animals.map(a => a.id).filter(Boolean);
        }

        if (animalIds.length === 0) {
            return;
        }

        const response = await journalStore.load({
            query: { animal: animalIds },
            skipUpdate: true,
            limit: 500,
        });
        saleStore.updateKeyValue('attestJournals', Array.isArray(response) ? response : []);
    }

    viewLog = (e) => {
        const { id } = e.target.closest('tr').dataset;
        const idInt = parseInt(id, 10);
        const { view } = this.state;
        this.setState({ view: view === idInt ? null : idInt });
    }

    onClickEditFile = e => {
        e.preventDefault();
        e.stopPropagation();
        const { id } = e.target.closest('tr').dataset;
        // console.log('onClickEditFile', id, parseInt(id, 10));
        const { drawerLevel = 1 } = this.props;
		const { appState, fileStore } = this.props.stores;
		const { drawerHeightMedium } = appState;
		appState.openDrawer('editFile', {
			height: drawerHeightMedium,
            id,
            callback: file => {
                appState.toggleDrawer(false, drawerLevel + 1);
            },
		}, drawerLevel + 2);
    }

    onClickDeleteFile = async e => {
        e.preventDefault();
        e.stopPropagation();
        const dialogAction = beginDialogAction(this, 'onClickDeleteFile');
        if (!dialogAction) return false;
        try {
            const dialogTargetData = { ...e.target.closest('tr').dataset };



            if (!((await appConfirm('Are you sure you want to delete this file?')) && dialogAction.isCurrent())) {
                return;
            }

            const { id } = dialogTargetData;
            const { fileStore } = this.props.stores;
            // console.log('onClickDeleteFile', id, parseInt(id, 10));
            await fileStore.delete(parseInt(id, 10));
            setTimeout(() => {
                this.loadAll(this.props, false);
            }, 1000);
        } finally {
            dialogAction.finish();
        }
    }

    onClickNewFile = e => {
        const { drawerLevel = 1 } = this.props;
		const { appState, filesStore } = this.props.stores;
		const { drawerHeightMedium } = appState;
		appState.openDrawer('newFile', {
			height: drawerHeightMedium,
            callback: () => {
                this.loadAll();
            },
		}, drawerLevel + 2);
	}

    onClickPreviewForm = e => {
        e.preventDefault();
        e.stopPropagation();
        const { id } = e.target.closest('tr').dataset;
        const { drawerLevel = 1, animalId } = this.props;
        const { appState, fileStore } = this.props.stores;
        const { drawerHeightMedium } = appState;
        appState.openDrawer('viewForm', {
            id,
            height: drawerHeightMedium,
            animalId,
            callback: () => {
                this.loadAll();
            },
        }, drawerLevel + 2);
    }

    onClickViewForm = e => {
        // console.log('onClickPrintSale', e);
        const { id } = e.target.closest('tr').dataset;

        const { animalId } = this.props;
        const { userStore } = this.props.stores;
        const { user } = userStore;
        const { language = 'no' } = user;
        const languageDef = countryMap[language || 'default'];

        // Render Receipt component with data
        const receipt = <IntlProvider definition={languageDef}>
            <DyrejournalPrintForm stores={this.props.stores} id={id} animalId={animalId} />
        </IntlProvider>;

        // Render to temporary div to capture HTML
        const tempDiv = document.createElement('div');
        tempDiv.className = 'viewForm';
        document.body.appendChild(tempDiv);
        render(receipt, tempDiv);
        const htmlContent = tempDiv.outerHTML;
        document.body.removeChild(tempDiv);
        render(null, tempDiv);
        // Trigger print dialog via iframe. Pass form-specific print CSS so the form
        // is scaled to fit a single A4 page even when a clinic logo is on top.
        printViaIframe(htmlContent, { customCss: FORM_PRINT_CSS });
    }

    onSearchInput = e => {
        const { value } = e.target;
        this.setState({ search: value });
    }

    componentDidMount() {
        this.loadAll();
        this.loadAttestJournals();
    }

    render() {
        const { drawerLevel = 1, animalId } = this.props;
        const { search } = this.state;
        const { appState, userStore, fileStore, saleStore } = this.props.stores;
        const { user = {}, isAdmin, isTester, isVeterinary } = userStore;
        const darkmode= util.getNestedValue(user, 'settings.darkmode');
        const { forms, newSale } = saleStore;
        const skjemaer = (forms || []).filter(f => f.type !== 'attest');
        const attester = (forms || []).filter(f => f.type === 'attest');
        const { files } = fileStore;

        const { visitors, animals, journals } = newSale;
        const currentVisitor = visitors && visitors.length > 0 ? visitors[0] : {};
        // Use animalId prop to find the correct animal, fall back to first if not found
        const animalIdInt = animalId ? parseInt(animalId, 10) : null;
        const currentAnimal = animalIdInt
            ? (animals && animals.find(a => a.id === animalIdInt)) || (animals && animals.length > 0 ? animals[0] : {})
            : (animals && animals.length > 0 ? animals[0] : {});
        const currentJournal = journals && journals.length > 0 ? journals[0] : {};
        const currentSale = newSale;

        const { language = 'no' } = user;
        const languageDef = countryMap[language || 'default'];

        return (<>
            <div class='w-100 h-100 d-flex justify-content-center'>
                <div class='w-100 h-100 px-2 pt-3' style='padding-top: 0px; padding-bottom: 100px;'>

                    <div class='w-100 d-flex flex-column'>
                        <div class='font-weight-lighter px-3 box-header d-flex flex-row justify-content-between'>
                            <Text id='admin.forms-section-skjemaer'>Skjemaer</Text>
                        </div>
                        <div class={`d-flex flex-column mt-1 mx-3 px-3 py-3 mb-4 rounded-lg ${darkmode ? 'bg-darkmode' : 'bg-lightmode'}`}>
                            <div class='table-responsive'>
                                <table class='table table-striped table-hover table-sm'>
                                    <thead>
                                        <tr>
                                            <th><Text id='admin.file-table-file'>File</Text></th>
                                            <th><Text id='admin.file-table-description'>Description</Text></th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {skjemaer.map((form, idx) => {
                                            return (<>
                                                <tr data-id={form.id} onClick={this.onClickViewForm}>
                                                    <td><nobr>{form.name}</nobr></td>
                                                    <td class='font-weight-lighter'>{form.description}</td>
                                                    <td onClick={this.onClickPreviewForm}><i class='fa-solid fa-eye' /></td>
                                                </tr>
                                            </>);
                                        })}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    </div>

                    {attester.length > 0 && <div class='w-100 d-flex flex-column'>
                        <div class='font-weight-lighter px-3 box-header d-flex flex-row justify-content-between'>
                            <Text id='admin.forms-section-attester'>Attester</Text>
                        </div>
                        <div class={`d-flex flex-column mt-1 mx-3 px-3 py-3 mb-4 rounded-lg ${darkmode ? 'bg-darkmode' : 'bg-lightmode'}`}>
                            <div class='table-responsive'>
                                <table class='table table-striped table-hover table-sm'>
                                    <thead>
                                        <tr>
                                            <th><Text id='admin.file-table-file'>File</Text></th>
                                            <th><Text id='admin.file-table-description'>Description</Text></th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {attester.map((form, idx) => {
                                            return (<>
                                                <tr data-id={form.id} onClick={this.onClickViewForm}>
                                                    <td><nobr>{form.name}</nobr></td>
                                                    <td class='font-weight-lighter'>{form.description}</td>
                                                    <td onClick={this.onClickPreviewForm}><i class='fa-solid fa-eye' /></td>
                                                </tr>
                                            </>);
                                        })}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    </div>}

                    <div class='w-100 d-flex flex-column'>
                        <div class='font-weight-lighter px-3 box-header d-flex flex-row justify-content-between'>
                            <Text id='admin.file-title'>File list</Text>
                            <div class='input-group w-50'>
                                <div class='input-group-text'>
                                    <span class='input-group-text rounded-pill-left'>
                                        <i class='fa-regular fa-magnifying-glass'></i>
                                    </span>
                                </div>
                                <input type='text' class='form-control' placeholder={`Search...`} onInput={this.onSearchInput} value={search} />
                                <div class='input-group-text'>
                                    <span class='input-group-text rounded-pill-right'>
                                        <i class='fa-solid fa-xmark' onClick={e => this.setState({ search: '' })} />
                                    </span>
                                </div>
                            </div>
                            <div>
                                <div class='float-right mb-1'>
                                    <button
                                        type='button'
                                        class='btn btn-sm btn-outline-secondary rounded-pill'
                                        onClick={this.onClickNewFile}
                                    >
                                        <i class='fa-solid fa-file text-muted' /> <Text id='form.file-new'>New File</Text>
                                    </button>
                                </div>
                            </div>
                        </div>
                        <div class={`d-flex flex-column mt-1 mx-3 px-3 py-3 mb-4 rounded-lg ${darkmode ? 'bg-darkmode' : 'bg-lightmode'}`}>
                            <div class='table-responsive'>
                                <table class='table table-striped table-hover table-sm'>
                                    <thead>
                                        <tr>
                                            <th>&nbsp;</th>
                                            <th><Text id='admin.file-table-date'>Date</Text></th>
                                            <th><Text id='admin.file-table-isFavorite'>isFavorite</Text></th>
                                            <th><Text id='admin.file-table-isPublic'>isPublic</Text></th>
                                            <th><Text id='admin.file-table-file'>File</Text></th>
                                            <th><Text id='admin.file-table-description'>Description</Text></th>
                                            <th>&nbsp;</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {files && files
                                           .filter(e =>
                                                !search ||
                                                e.name.toLowerCase().includes(search.toLowerCase())
                                            )
                                            .map((file, idx) => {
                                            return (<>
                                                <tr data-id={file.id}>
                                                    {file.customer === user.customer ? <>
                                                        <td class='d-none d-sm-table-cell' onClick={this.onClickEditFile} data-id={file.id}>
                                                            <i class='fa-solid fa-pen' />
                                                        </td>
                                                    </> : <>
                                                        <td class='d-none d-sm-table-cell'>&nbsp;</td>
                                                    </>}
                                                    <td><nobr>{util.isoDate(file.date)}</nobr></td>
                                                    <td align='center'><nobr>{file.isFavorite ? <i class='fa-duotone fa-check' /> : ''}</nobr></td>
                                                    <td align='center'><nobr>{file.isPublic ? <i class='fa-duotone fa-check' /> : ''}</nobr></td>
                                                    <td><nobr>{file.name}</nobr></td>
                                                    <td class='font-weight-lighter'>{file.description}</td>
                                                    <td class='font-weight-lighter'>
                                                        <FileList
                                                            files={file.files}
                                                            id={file.id}
                                                            stores={this.props.stores}
                                                            drawerLevel={drawerLevel}
                                                            currentVisitor={currentVisitor}
                                                            currentAnimal={currentAnimal}
                                                            currentJournal={currentJournal}
                                                            currentSale={currentSale}
                                                            saleId={newSale.id}
                                                        />
                                                    </td>
                                                    {file.customer === user.customer ? <>
                                                        <td class='d-none d-sm-table-cell' onClick={this.onClickDeleteFile} data-id={file.id}>
                                                            {file.isPublic ? <></> : <>
                                                                <i class='fa-solid fa-trash text-danger' />
                                                            </>}
                                                        </td>
                                                    </> : <>
                                                        <td class='d-none d-sm-table-cell'>&nbsp;</td>
                                                    </>}
                                                </tr>
                                            </>);
                                        })}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    </div>

                </div>
            </div>
        </>);
    }
}

export default FileListTable;
